/* eslint-disable max-lines */
import {
  ArrowRight,
  CheckCircle,
  CircleNotch,
  Cube,
  Palette,
  ShieldChevron,
  Sparkle,
  WarningCircle,
} from '@phosphor-icons/react';
import { Card, Stack, Text } from '@fragments-sdk/ui';
import type { ReactNode } from 'react';
import type { DashboardDrillTarget } from './dashboard-brief';
import type {
  DashboardAutomationGate,
  DashboardFixScenario,
  DashboardFixScenarioState,
  DashboardMetrics,
  DashboardScannerEvidence,
} from './dashboard-surface';
import s from './dashboard.module.scss';

export interface SetupStep {
  title: string;
  description: string;
  actionLabel: string;
  target?: DashboardDrillTarget;
  tourTarget: string;
  illustration?: ReactNode;
  onActivate?: () => void;
  disabled?: boolean;
  busy?: boolean;
  hideAction?: boolean;
  tone?: 'default' | 'success' | 'danger';
  cardTone?: 'accent' | 'component' | 'token' | 'governance';
  statusMessage?: string;
  statusTone?: 'busy' | 'success' | 'danger' | 'warning';
}

export const DEFAULT_AUTOMATION_GATE: DashboardAutomationGate = {
  enabled: true,
  currentPlanLabel: 'Pro',
  requiredPlanLabel: 'Pro',
};

export function canStepActivate(
  step: SetupStep,
  onNavigate?: (target: DashboardDrillTarget) => void,
): boolean {
  return Boolean(step.onActivate) || Boolean(onNavigate && step.target);
}

export function resolveStepActivate(
  step: SetupStep,
  onNavigate?: (target: DashboardDrillTarget) => void,
): () => void {
  return () => {
    if (step.disabled) return;
    if (step.onActivate) {
      step.onActivate();
      return;
    }
    if (step.target) onNavigate?.(step.target);
  };
}

// ── Setup checklist (pre-governance mode) ────────────────────────

export function SetupSteps({
  findingsOpen,
  decisions,
  componentCount,
  tokenCount,
  unclassified,
  scannerEvidence,
  myPrFindings,
  fixScenario,
  fixScenarioState,
  automationGate = DEFAULT_AUTOMATION_GATE,
  onNavigate,
  onCreateFixScenario,
}: {
  findingsOpen: number;
  decisions: DashboardMetrics['decisionIntelligence'];
  componentCount: number;
  tokenCount: number;
  unclassified: number;
  scannerEvidence?: DashboardScannerEvidence;
  myPrFindings: DashboardMetrics['myPrFindings'];
  fixScenario: DashboardFixScenario | null;
  fixScenarioState?: DashboardFixScenarioState;
  automationGate?: DashboardAutomationGate;
  onNavigate?: (target: DashboardDrillTarget) => void;
  onCreateFixScenario?: (scenario: DashboardFixScenario) => void;
}) {
  const recommendedStep = getRecommendedStep({
    findingsOpen,
    decisions,
    componentCount,
    tokenCount,
    fixScenario,
    fixScenarioState,
    myPrFindings,
    governanceConfigured: false,
    automationGate,
    onCreateFixScenario,
  });

  const hasRepo = componentCount > 0 || tokenCount > 0;
  const componentsReviewed = componentCount > 0 && unclassified === 0;

  // Self-dismissing checklist — only steps that still need attention render.
  const componentsStep: SetupStep | null = componentsReviewed
    ? null
    : {
        title: 'Review indexed components',
        description: `${formatCount(componentCount)} found. Confirm primitives, compositions, and ownership.`,
        actionLabel: 'Review',
        target: 'findings',
        tourTarget: 'setup-components',
        illustration: <Cube size={34} weight="duotone" />,
        cardTone: 'component',
        statusMessage: scannerEvidenceStatus(scannerEvidence),
        statusTone: scannerEvidence?.diagnostics.length ? 'warning' : 'success',
      };
  const tokensStep: SetupStep = {
    title: 'Enrich discovered tokens',
    description: `${formatCount(tokenCount)} found. Map color, spacing, typography, and intent.`,
    actionLabel: 'Review',
    target: 'findings',
    tourTarget: 'setup-tokens',
    illustration: <Palette size={34} weight="duotone" />,
    cardTone: 'token',
  };
  const governanceStep = buildGovernanceStep(automationGate);

  const checklist: SetupStep[] = [];
  if (componentsStep) checklist.push(componentsStep);
  if (tokenCount > 0) checklist.push(tokensStep);

  // Lead with governance unless the smart recommendation already targets it,
  // or there's no repo yet (governance can't function without one).
  const noRepoYet = !hasRepo;
  const steps: SetupStep[] =
    recommendedStep.target === 'governance' || noRepoYet
      ? [recommendedStep, ...checklist]
      : [governanceStep, recommendedStep, ...checklist];

  const milestonesDone =
    (hasRepo ? 1 : 0) + (componentsReviewed ? 1 : 0);
  const milestoneTotal = 3;

  return (
    <Stack gap="sm">
      <SetupProgress done={milestonesDone} total={milestoneTotal} />
      <ul className="fs-cdp-setupList" aria-label="Next steps">
        {steps.map((step) => (
          <SetupRow key={step.title} step={step} onNavigate={onNavigate} />
        ))}
      </ul>
    </Stack>
  );
}

function SetupProgress({ done, total }: { done: number; total: number }) {
  return (
    <Stack
      direction="row"
      gap="sm"
      align="center"
      justify="between"
      wrap
      className={s.setupProgress}
    >
      <Text as="span" size="sm" weight="medium" color="secondary">
        Finish setup to start catching drift in CI
      </Text>
      <Stack direction="row" gap="xs" align="center">
        <span className={s.setupDots} aria-hidden="true">
          {Array.from({ length: total }).map((_, index) => (
            <span
              key={index}
              className={s.setupDot}
              data-filled={index < done || undefined}
            />
          ))}
        </span>
        <Text as="span" size="xs" weight="medium" color="tertiary" tabularNums>
          {done} of {total} done
        </Text>
      </Stack>
    </Stack>
  );
}

// ── Recommended step selection (shared by setup + operational) ────

export function getRecommendedStep({
  findingsOpen,
  decisions,
  componentCount,
  tokenCount,
  fixScenario,
  fixScenarioState,
  myPrFindings,
  governanceConfigured,
  automationGate,
  onCreateFixScenario,
}: {
  findingsOpen: number;
  decisions: DashboardMetrics['decisionIntelligence'];
  componentCount: number;
  tokenCount: number;
  fixScenario: DashboardFixScenario | null;
  fixScenarioState?: DashboardFixScenarioState;
  myPrFindings: DashboardMetrics['myPrFindings'];
  governanceConfigured: boolean;
  automationGate: DashboardAutomationGate;
  onCreateFixScenario?: (scenario: DashboardFixScenario) => void;
}): SetupStep {
  // No repo scan has populated anything yet — push the user toward connecting
  // a repository instead of governance setup, which can't function without one.
  if (
    !fixScenario &&
    findingsOpen === 0 &&
    decisions.total === 0 &&
    componentCount === 0 &&
    tokenCount === 0 &&
    !governanceConfigured
  ) {
    return {
      title: 'Connect a repository',
      description:
        'Bind a repo to populate components, tokens, and findings. Governance comes next.',
      actionLabel: 'Connect repository',
      target: 'settings',
      tourTarget: 'setup-recommended',
      illustration: <Sparkle size={34} weight="duotone" />,
      cardTone: 'accent',
    };
  }

  if (fixScenario) {
    if (!automationGate.enabled) {
      return {
        title: 'Upgrade to create fix PRs',
        description: `${automationGate.currentPlanLabel} workspaces can review findings manually. ${automationGate.requiredPlanLabel} unlocks GitHub automation, CI reporting, PR comments, and autofix pull requests.`,
        actionLabel: 'View plans',
        target: 'billing',
        tourTarget: 'setup-recommended',
        illustration: <Sparkle size={34} weight="duotone" />,
        cardTone: 'accent',
        statusMessage:
          'No repository changes will be attempted until automation is enabled.',
        statusTone: 'warning',
      };
    }

    const status = fixScenarioState?.status ?? 'idle';
    const fileName = disambiguatedPath(fixScenario.filePath);
    const count = formatCount(fixScenario.fixableCount);
    const isDecisionBatch = fixScenario.kind === 'autofix_decision';
    const openPrCount = myPrFindings.open;
    const hasOpenPrs = openPrCount > 0;
    const targetLabel = isDecisionBatch
      ? (fixScenario.decisionTitle ?? 'top safe decision')
      : fileName;
    const hasFixableIds =
      fixScenario.fixableCount > 0 && fixScenario.findingIds.length > 0;
    const noFixableError =
      status === 'error' &&
      (fixScenarioState?.error ?? '').toLowerCase().includes('no fixable');
    const quotaExceededError =
      status === 'error' &&
      (fixScenarioState?.error ?? '').toLowerCase().includes('quota exceeded');

    if (status === 'success') {
      return {
        title: 'Fix PR created',
        description: `Opened PR #${fixScenarioState?.prNumber ?? ''} with ${formatCount(fixScenarioState?.fixedCount ?? fixScenario.fixableCount)} replacement${(fixScenarioState?.fixedCount ?? fixScenario.fixableCount) === 1 ? '' : 's'} for ${targetLabel}.`,
        actionLabel: 'Open PR',
        tourTarget: 'setup-recommended',
        illustration: <Sparkle size={34} weight="duotone" />,
        cardTone: 'accent',
        onActivate: () => {
          if (fixScenarioState?.prUrl) {
            window.open(
              fixScenarioState.prUrl,
              '_blank',
              'noopener,noreferrer',
            );
          }
        },
        tone: 'success',
        statusMessage: 'Draft pull request is ready in GitHub.',
        statusTone: 'success',
      };
    }

    if (isDecisionBatch) {
      return {
        title: hasOpenPrs
          ? `${formatCount(openPrCount)} open PR${openPrCount === 1 ? '' : 's'} ready for review`
          : 'Review safe decision batch',
        description:
          status === 'error'
            ? (fixScenarioState?.error ??
              'The fix PR could not be created. Review the findings and try again.')
            : `${count} safe fix${fixScenario.fixableCount === 1 ? '' : 'es'} for ${targetLabel}. Review the findings queue before batching changes into a pull request.`,
        actionLabel: 'Review findings',
        target: 'findings',
        tourTarget: 'setup-recommended',
        illustration: <Sparkle size={34} weight="duotone" />,
        cardTone: 'accent',
        tone: status === 'error' ? 'danger' : 'default',
        statusMessage: hasOpenPrs
          ? `${formatCount(openPrCount)} open PR${openPrCount === 1 ? '' : 's'} ready for review.`
          : undefined,
        statusTone: hasOpenPrs ? 'warning' : undefined,
      };
    }

    if (!hasFixableIds || noFixableError) {
      return {
        title: 'Fix one file with AI',
        description:
          'Fragments could not find a safe one-file fix in the current scan.',
        actionLabel: 'Review findings',
        target: 'findings',
        tourTarget: 'setup-recommended',
        illustration: <Sparkle size={34} weight="duotone" />,
        cardTone: 'accent',
        tone: 'danger',
        statusMessage:
          fixScenarioState?.error ??
          'No autofixable token findings are available yet. Review findings or run a fresh scan.',
        statusTone: 'danger',
      };
    }

    return {
      title: 'Fix one file with AI',
      description:
        status === 'error'
          ? (fixScenarioState?.error ??
            'The fix PR could not be created. Try again or review the findings.')
          : `${count} autofixable finding${fixScenario.fixableCount === 1 ? '' : 's'} in ${fileName}. Fragments will create a focused pull request through the same autofix pipeline.`,
      actionLabel:
        status === 'running'
          ? 'Creating PR'
          : quotaExceededError
            ? 'Quota exceeded'
            : 'Create fix PR',
      tourTarget: 'setup-recommended',
      illustration: <Sparkle size={34} weight="duotone" />,
      cardTone: 'accent',
      onActivate: () => onCreateFixScenario?.(fixScenario),
      disabled:
        status === 'running' || quotaExceededError || !onCreateFixScenario,
      busy: status === 'running',
      hideAction: status === 'running',
      tone: status === 'error' ? 'danger' : 'default',
      statusMessage:
        status === 'running'
          ? 'Creating a branch, applying fixes, and opening a draft PR...'
          : status === 'error'
            ? (fixScenarioState?.error ??
              'The fix PR could not be created. Nothing changed in the repository.')
            : undefined,
      statusTone:
        status === 'running'
          ? 'busy'
          : status === 'error'
            ? 'danger'
            : undefined,
    };
  }

  if (decisions.policyNeeded > 0) {
    return {
      title: 'Confirm policy readiness',
      description: `${formatCount(decisions.policyNeeded)} design-system decision${decisions.policyNeeded === 1 ? '' : 's'} need policy before Cloud should score them as violations.`,
      actionLabel: 'Configure policy',
      target: 'governance',
      tourTarget: 'setup-recommended',
      illustration: <Sparkle size={34} weight="duotone" />,
      cardTone: 'accent',
    };
  }

  if (decisions.total > 0) {
    return {
      title: 'Review design decisions',
      description:
        decisions.topTitle ??
        `${formatCount(decisions.total)} design-system decision${decisions.total === 1 ? '' : 's'} need review.`,
      actionLabel: 'Review decisions',
      target: 'findings',
      tourTarget: 'setup-recommended',
      illustration: <Sparkle size={34} weight="duotone" />,
      cardTone: 'accent',
    };
  }

  if (findingsOpen > 0) {
    return {
      title: 'Recommended next action',
      description: `${formatCount(findingsOpen)} open. Start with the highest-impact findings before they spread.`,
      actionLabel: 'Review findings',
      target: 'findings',
      tourTarget: 'setup-recommended',
      illustration: <Sparkle size={34} weight="duotone" />,
      cardTone: 'accent',
    };
  }

  if (!governanceConfigured) {
    if (!automationGate.enabled) {
      return {
        title: 'Unlock governance automation',
        description: `${automationGate.currentPlanLabel} includes manual findings review. ${automationGate.requiredPlanLabel} adds CI reporting, PR feedback, and Cloud-managed policies.`,
        actionLabel: 'View plans',
        target: 'billing',
        tourTarget: 'setup-recommended',
        illustration: <Sparkle size={34} weight="duotone" />,
        cardTone: 'accent',
      };
    }

    return {
      title: 'Recommended next action',
      description:
        'Connect governance next so new design-system issues are caught in CI.',
      actionLabel: 'Set up governance',
      target: 'governance',
      tourTarget: 'setup-recommended',
      illustration: <Sparkle size={34} weight="duotone" />,
      cardTone: 'accent',
    };
  }

  if (componentCount > 0) {
    return {
      title: 'Recommended next action',
      description:
        'Review component ownership so generated UI can reuse the right primitives.',
      actionLabel: 'Review components',
      target: 'findings',
      tourTarget: 'setup-recommended',
      illustration: <Sparkle size={34} weight="duotone" />,
      cardTone: 'accent',
    };
  }

  if (tokenCount > 0) {
    return {
      title: 'Recommended next action',
      description:
        'Map discovered tokens into intent so agents have clear styling guidance.',
      actionLabel: 'Review tokens',
      target: 'findings',
      tourTarget: 'setup-recommended',
      illustration: <Sparkle size={34} weight="duotone" />,
      cardTone: 'accent',
    };
  }

  return {
    title: 'Recommended next action',
    description: 'Review the setup areas to build a stronger system map.',
    actionLabel: 'Review components',
    target: 'findings',
    tourTarget: 'setup-recommended',
    illustration: <Sparkle size={34} weight="duotone" />,
    cardTone: 'accent',
  };
}

export function buildGovernanceStep(
  automationGate: DashboardAutomationGate,
): SetupStep {
  if (!automationGate.enabled) {
    return {
      title: 'Governance automation',
      description: `${automationGate.requiredPlanLabel} unlocks workflow setup, GitHub feedback, and Cloud-managed policy sync.`,
      actionLabel: 'View plans',
      target: 'billing',
      tourTarget: 'setup-governance',
      illustration: <ShieldChevron size={34} weight="duotone" />,
      cardTone: 'governance',
    };
  }

  return {
    title: 'Set up governance',
    description: 'Connect CI to catch design system issues before they ship.',
    actionLabel: 'Get started',
    target: 'governance',
    tourTarget: 'setup-governance',
    illustration: <ShieldChevron size={34} weight="duotone" />,
    cardTone: 'governance',
  };
}

// ── Setup card renderer ──────────────────────────────────────────

export function SetupRow({
  step,
  onNavigate,
}: {
  step: SetupStep;
  onNavigate?: (target: DashboardDrillTarget) => void;
}) {
  const canActivate = canStepActivate(step, onNavigate);
  const activate = resolveStepActivate(step, onNavigate);
  return (
    <li className={s.setupListItem}>
      <Card
        as="article"
        variant="outlined"
        padding="none"
        className={`fs-cdp-setupCard ${s.setupRow}`}
        data-setup-card
        data-tour={step.tourTarget}
        data-has-illustration={Boolean(step.illustration) || undefined}
        data-interactive={canActivate || undefined}
        data-state={step.tone}
        data-tone={step.cardTone ?? 'accent'}
        data-disabled={step.disabled || undefined}
        aria-label={
          canActivate ? `${step.actionLabel}: ${step.title}` : undefined
        }
        aria-disabled={step.disabled || undefined}
        onClick={canActivate && !step.disabled ? activate : undefined}
      >
        {step.illustration ? (
          <span className="fs-cdp-setupIllustration" aria-hidden="true">
            {step.illustration}
          </span>
        ) : null}
        <Stack className={s.setupText} gap="xs">
          <Text
            as="span"
            size="sm"
            weight="semibold"
            className="fs-cdp-setupTitle"
          >
            {step.title}
          </Text>
          <Text
            as="span"
            size="xs"
            color="secondary"
            className="fs-cdp-setupDesc"
          >
            {step.description}
          </Text>
          {step.statusMessage ? (
            <SetupStatus
              message={step.statusMessage}
              tone={step.statusTone}
            />
          ) : null}
        </Stack>
        {!step.hideAction ? <SetupAction step={step} /> : null}
      </Card>
    </li>
  );
}

export function SetupStatus({
  message,
  tone,
}: {
  message: string;
  tone?: SetupStep['statusTone'];
}) {
  return (
    <Text
      as="span"
      size="xs"
      className={s.setupStatus}
      data-tone={tone}
      role={tone === 'danger' ? 'alert' : 'status'}
    >
      {tone === 'busy' ? (
        <CircleNotch size={13} className={s.spinIcon} />
      ) : tone === 'success' ? (
        <CheckCircle size={13} />
      ) : tone === 'danger' ? (
        <WarningCircle size={13} />
      ) : null}
      {message}
    </Text>
  );
}

export function SetupAction({ step }: { step: SetupStep }) {
  return (
    <Text
      as="span"
      size="xs"
      weight="semibold"
      className={`fs-cdp-setupAction ${s.setupBtn}`}
    >
      {step.busy ? <CircleNotch size={13} className={s.spinIcon} /> : null}
      {step.tone === 'success' && !step.busy ? <CheckCircle size={13} /> : null}
      {step.tone === 'danger' && !step.busy ? <WarningCircle size={13} /> : null}
      {step.actionLabel}
      {!step.busy && step.tone !== 'success' && step.tone !== 'danger' ? (
        <ArrowRight size={13} />
      ) : null}
    </Text>
  );
}

// ── Helpers ──────────────────────────────────────────────────────

export function scannerEvidenceStatus(
  evidence: DashboardScannerEvidence | undefined,
): string | undefined {
  if (!evidence?.available) return undefined;
  const factCount =
    evidence.components.length +
    evidence.tokens.length +
    evidence.diagnostics.length +
    evidence.componentAdoption.length +
    evidence.tokenAdoption.length;
  const branch = evidence.branch ?? 'latest scan';
  const diagnostics =
    evidence.diagnostics.length > 0
      ? ` · ${formatCount(evidence.diagnostics.length)} scanner diagnostic${evidence.diagnostics.length === 1 ? '' : 's'}`
      : '';
  return `${formatCount(factCount)} evidence sample${factCount === 1 ? '' : 's'} from ${branch}${diagnostics}`;
}

export function disambiguatedPath(path: string): string {
  const parts = path.split('/').filter(Boolean);
  if (parts.length <= 2) return path;
  return `${parts.at(-2)}/${parts.at(-1)}`;
}

export function formatCount(count: number): string {
  if (!Number.isFinite(count)) return '0';
  return new Intl.NumberFormat().format(count);
}
